import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { decodePrefillToken } from 'services/emailService';
import { useBooking } from 'contexts/BookingContext';
import { format } from 'date-fns';
import Toast from 'components/ux/toast/Toast';
import { COUNTRIES } from 'constants/countries';

// Fallback room type → Mews category ID mapping (production).
// Server config (MEWS_ROOM_TYPE_MAP env var) overrides these when available.
const ROOM_TYPE_TO_CATEGORY_FALLBACK = {
  family: '0a773bbc-438f-4ab9-884d-b44700db7d25',
  double: '2988244f-cc0a-4199-8f63-b44700d732cf',
  'kirsebaekhuset-family': 'c769725b-2b81-4d74-b69b-b44900ec7c18',
  'kirsebaekhuset-double': 'b3952a5c-efae-4e5a-90eb-b44f00e0c87b',
};

const ROOM_TYPE_LABELS = {
  family: 'Family Room',
  double: 'Double Room',
  'kirsebaekhuset-family': 'Kirsebærhuset Family',
  'kirsebaekhuset-double': 'Kirsebærhuset Double',
};

/**
 * Call the import API endpoints (no login required — prefill token is the auth).
 */
async function importRequest(endpoint, body) {
  const res = await fetch(`/api/import/${endpoint}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await res.json();
  if (!res.ok) {
    const err = new Error(data.error || `Request failed: ${res.status}`);
    err.status = res.status;
    err.detail = data.detail;
    throw err;
  }
  return data;
}

const Import = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { dispatch: bookingDispatch } = useBooking();

  // The raw JWT token from the URL
  const [token, setToken] = useState('');

  // Parsed booking data from token
  const [parsed, setParsed] = useState(null);
  const [tokenError, setTokenError] = useState('');

  // Server-side config & availability
  const [config, setConfig] = useState(null);
  const [availabilityStatus, setAvailabilityStatus] = useState('loading');

  // Editable form state
  const [poReference, setPoReference] = useState('');
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [roomLines, setRoomLines] = useState([]);
  const [mainGuest, setMainGuest] = useState({ firstName: '', lastName: '', phone: '', nationalityCode: '' });
  const [guests, setGuests] = useState([]);
  const [breakfastCount, setBreakfastCount] = useState(0);
  const [notes, setNotes] = useState('');

  // UI state
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [confirmation, setConfirmation] = useState(null);

  // Decode token on mount
  useEffect(() => {
    const t = searchParams.get('token');
    if (!t) {
      setTokenError('No booking token found in URL.');
      return;
    }
    setToken(t);

    try {
      const data = decodePrefillToken(t);
      if (!data) {
        setTokenError('Invalid or expired booking token.');
        return;
      }
      setParsed(data);

      // Populate editable fields
      setPoReference(data.poReference || '');
      setCheckIn(data.checkIn || '');
      setCheckOut(data.checkOut || '');
      setMainGuest(data.mainGuest || { firstName: '', lastName: '', phone: '', nationalityCode: '' });
      setGuests(data.guests || []);
      setBreakfastCount(data.breakfastCount || 0);
      setNotes(data.notes || '');

      // Map room lines with Mews category IDs (will be re-mapped with server config later)
      const mapped = (data.roomLines || []).map((rl) => ({
        ...rl,
        categoryId: ROOM_TYPE_TO_CATEGORY_FALLBACK[rl.roomType] || '',
        categoryName: ROOM_TYPE_LABELS[rl.roomType] || rl.roomTypeRaw || rl.roomType,
      }));
      setRoomLines(mapped);
    } catch {
      setTokenError('Invalid or expired booking token.');
    }
  }, [searchParams]);

  // Load config + check availability via the import preview endpoint
  const loadPreview = async () => {
    if (!token) return;
    try {
      setAvailabilityStatus('loading');
      setError('');

      const result = await importRequest('preview', { token });
      setConfig(result.config);

      // Re-map room line category IDs using server-provided mapping (overrides fallback)
      const serverMap = result.config?.roomTypeMap || {};
      let updatedLines = roomLines.map((rl) => ({
        ...rl,
        categoryId: (Object.keys(serverMap).length > 0 ? serverMap[rl.roomType] : null) || rl.categoryId,
      }));

      // Check availability — slice to actual stay nights (Mews includes checkout boundary)
      const catAvail = result.availability?.CategoryAvailabilities || [];
      const stayNights = nights || 1;
      const catMap = {};
      catAvail.forEach((ca) => {
        const avail = (ca.Availabilities || []).slice(0, stayNights);
        const catId = ca.CategoryId || ca.ResourceCategoryId;
        catMap[catId] = avail.length > 0 ? Math.min(...avail) : 0;
      });

      // Aggregate requested quantities per category before checking
      const requested = {};
      for (const rl of updatedLines) {
        if (rl.categoryId) {
          requested[rl.categoryId] = (requested[rl.categoryId] || 0) + rl.quantity;
        }
      }
      let allAvailable = true;
      for (const [catId, qty] of Object.entries(requested)) {
        if ((catMap[catId] ?? 0) < qty) {
          allAvailable = false;
          break;
        }
      }
      // Also check for unmapped room types
      if (updatedLines.some((rl) => !rl.categoryId)) {
        allAvailable = false;
      }

      setAvailabilityStatus(allAvailable ? 'available' : 'unavailable');

      // Enrich room lines with pricing from Mews rate data
      if (result.pricing?.CategoryPrices) {
        const nordicRateId = result.config?.nordicRate?.Id;
        const pricingCurrency = result.config?.enterprise?.currency || 'DKK';
        const nightCount = checkIn && checkOut
          ? Math.ceil((new Date(checkOut) - new Date(checkIn)) / (1000 * 60 * 60 * 24))
          : 1;
        updatedLines = updatedLines.map((rl) => {
          const catPricing = result.pricing.CategoryPrices.find(
            (cp) => (cp.CategoryId || cp.ResourceCategoryId) === rl.categoryId
          );
          const prices = catPricing?.Prices || [];
          const amountPrices = catPricing?.AmountPrices || [];
          if (prices.length > 0) {
            const slice = prices.slice(0, nightCount);
            const total = slice.reduce((sum, p) => {
              if (typeof p === 'number') return sum + p;
              return sum + (p.GrossValue || p.TotalAmount?.Value || 0);
            }, 0);
            const perNight = nightCount > 0 ? total / nightCount : 0;
            const currency = amountPrices[0]?.Currency || pricingCurrency;
            return { ...rl, pricePerNight: perNight, totalPrice: total, currency, rateId: nordicRateId || rl.rateId || '' };
          }
          return { ...rl, rateId: nordicRateId || rl.rateId || '' };
        });
      }

      setRoomLines(updatedLines);
    } catch (err) {
      if (err.status === 401) {
        setTokenError('This booking link has expired. Please request a new one.');
        return;
      }
      setError(`Failed to check availability: ${err.message}`);
      setAvailabilityStatus('error');
    }
  };

  const checkedRef = useRef(false);
  useEffect(() => {
    if (parsed && token && !checkedRef.current) {
      checkedRef.current = true;
      loadPreview();
    }
  }, [parsed, token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!poReference.trim()) {
      setError('PO reference is required.');
      return;
    }
    if (!mainGuest.firstName.trim() || !mainGuest.lastName.trim()) {
      setError('Main guest name is required.');
      return;
    }
    if (!checkIn || !checkOut) {
      setError('Check-in and check-out dates are required.');
      return;
    }
    if (new Date(checkOut) <= new Date(checkIn)) {
      setError('Check-out must be after check-in.');
      return;
    }
    if (roomLines.length === 0) {
      setError('At least one room is required.');
      return;
    }
    const unmapped = roomLines.find((rl) => !rl.categoryId);
    if (unmapped) {
      setError(`Room type "${unmapped.categoryName || unmapped.roomType}" could not be mapped. Please use the manual booking flow.`);
      return;
    }

    try {
      setSubmitting(true);

      const result = await importRequest('confirm', {
        token,
        mainGuest,
        guests,
        roomLines,
        poReference,
        checkIn,
        checkOut,
        notes: notes || undefined,
      });

      setConfirmation({
        ...result,
        poReference,
        mainGuest,
        roomLines,
        checkIn,
        checkOut,
      });
    } catch (err) {
      if (err.status === 401) {
        setTokenError('This booking link has expired. Please request a new one.');
        return;
      }
      setError(`Booking failed: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const updateGuest = (index, updates) => {
    setGuests((prev) => prev.map((g, i) => (i === index ? { ...g, ...updates } : g)));
  };

  const removeGuest = (index) => {
    setGuests((prev) => prev.filter((_, i) => i !== index));
  };

  const addGuest = () => {
    setGuests((prev) => [...prev, { firstName: '', lastName: '', birthDate: '', isChild: false }]);
  };

  const handleBookManually = () => {
    // Prefill BookingContext with parsed data and navigate to regular booking flow
    if (checkIn && checkOut) {
      bookingDispatch({ type: 'SET_DATES', checkIn: new Date(checkIn), checkOut: new Date(checkOut) });
    }
    if (poReference) {
      bookingDispatch({ type: 'SET_PO_REFERENCE', poReference });
    }
    bookingDispatch({ type: 'SET_MAIN_GUEST', guest: mainGuest });
    bookingDispatch({ type: 'SET_GUESTS', guests });
    if (notes) {
      bookingDispatch({ type: 'SET_NOTES', notes });
    }
    navigate('/');
  };

  const nights = checkIn && checkOut
    ? Math.ceil((new Date(checkOut) - new Date(checkIn)) / (1000 * 60 * 60 * 24))
    : 0;
  const totalRooms = roomLines.reduce((sum, rl) => sum + rl.quantity, 0);

  // Warning: main booker not in guest list (unusual — the main booker is almost always a guest)
  // The parser dedups the main booker OUT of guests[], so checking that array alone
  // would always warn. parsed.bookerInGuestList records whether they appeared in the email.
  const mainGuestName = `${mainGuest.firstName} ${mainGuest.lastName}`.trim().toLowerCase();
  const mainGuestInList = guests.some(
    (g) => `${g.firstName} ${g.lastName}`.trim().toLowerCase() === mainGuestName
  );
  const showBookerNotGuestWarning =
    mainGuestName && guests.length > 0 && !mainGuestInList && !parsed?.bookerInGuestList;

  // Warning: more rooms than adults — server enforces min 1 adult per room in Mews
  const adultCount = guests.filter((g) => !g.isChild).length + (mainGuestName ? 1 : 0);
  const showRoomsExceedAdultsWarning = totalRooms > adultCount;

  // Warning: email price vs Mews rate mismatch
  const priceMismatches = roomLines
    .filter((rl) => rl.priceDKK && rl.totalPrice && Math.abs(rl.priceDKK - rl.totalPrice * rl.quantity) > 1)
    .map((rl) => ({
      name: rl.categoryName || rl.roomTypeRaw || rl.roomType,
      emailPrice: Math.round(rl.priceDKK),
      mewsPrice: Math.round(rl.totalPrice * rl.quantity),
    }));

  // Token error
  if (tokenError) {
    return (
      <div className="max-w-lg mx-auto px-4 py-16 text-center">
        <div className="text-4xl text-red-400 mb-4">!</div>
        <h1 className="text-xl font-medium text-brand-dark mb-2">Invalid Booking Link</h1>
        <p className="text-brand-muted mb-6">{tokenError}</p>
      </div>
    );
  }

  // Not yet parsed
  if (!parsed) {
    return (
      <div className="max-w-lg mx-auto px-4 py-16 text-center">
        <p className="text-brand-muted">Loading booking data...</p>
      </div>
    );
  }

  // Unavailable — show summary and redirect to manual booking
  if (availabilityStatus === 'unavailable') {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="flex items-center gap-3 mb-1">
          <span className="text-xs font-medium tracking-wide uppercase px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
            Email Import
          </span>
        </div>
        <h1 className="text-2xl font-medium text-brand-dark mb-6">Rooms Not Available</h1>

        <div className="mb-6 p-5 bg-red-50 border border-red-200 rounded-lg">
          <p className="font-medium text-red-800 mb-3">The requested rooms are not available for these dates.</p>
          <div className="text-sm text-red-700 space-y-1 mb-1">
            <p><strong>Dates:</strong> <span className="font-mono">{checkIn} &rarr; {checkOut}</span> ({nights} night{nights !== 1 ? 's' : ''})</p>
            {roomLines.map((rl, i) => (
              <p key={i}><strong>Room:</strong> {rl.quantity}&times; {rl.categoryName}</p>
            ))}
            {poReference && <p><strong>PO:</strong> <span className="font-mono">{poReference}</span></p>}
          </div>
        </div>

        <div className="card p-6 text-center">
          <p className="text-brand-muted text-sm mb-4">
            Use the booking portal to try different dates or room types.
            Your booking details will be prefilled.
          </p>
          <button onClick={handleBookManually} className="btn-gold">
            Book with different dates / room types
          </button>
        </div>

        <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm">
          <p className="text-amber-800">
            <strong>Need help?</strong>{' '}
            <a href="mailto:thomas@kirsebaerkroen.dk" className="underline hover:no-underline">
              Contact us
            </a>{' '}
            for assistance.
          </p>
        </div>
      </div>
    );
  }

  // Confirmation page
  if (confirmation) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="bg-brand-teal/10 border border-brand-teal/30 rounded-lg p-6 text-center mb-8">
          <div className="text-3xl mb-2 text-brand-teal">&#10003;</div>
          <h1 className="text-2xl font-medium text-brand-dark mb-2">Booking Confirmed</h1>
          <p className="text-brand-muted">
            Nordic PO: <strong className="text-lg font-mono text-brand-dark">{poReference}</strong>
          </p>
        </div>

        {confirmation.companionErrors?.length > 0 && (
          <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 mb-8">
            <p className="font-medium text-amber-800 text-sm">Some guests could not be registered in a room</p>
            <p className="text-sm text-amber-700 mt-1">
              The booking itself is confirmed, but Mews refused to add{' '}
              <strong>{confirmation.companionErrors.map((e) => e.name).join(', ')}</strong> to any of the rooms.
              Please contact the hotel so they can add the guest manually.
            </p>
          </div>
        )}

        <div className="card p-6">
          <div className="grid grid-cols-2 gap-x-8 gap-y-4 mb-6">
            <Detail label="PO Reference" value={poReference} mono />
            <Detail label="Status" value="Confirmed" />
            <Detail label="Check-in" value={checkIn} mono />
            <Detail label="Check-out" value={checkOut} mono />
            <Detail label="Rooms" value={totalRooms} mono />
            <Detail label="Nights" value={nights} mono />
          </div>

          {confirmation.Reservations?.length > 0 && (
            <div className="mb-4">
              <h3 className="text-xs font-medium text-brand-muted uppercase tracking-wide mb-2">Mews References</h3>
              <div className="text-xs text-brand-muted space-y-1 font-mono">
                {confirmation.Reservations.map((r, i) => (
                  <p key={i}>#{r.Number || i + 1}: {r.Id}</p>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <div className="flex items-center gap-3 mb-1">
        <span className="text-xs font-medium tracking-wide uppercase px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
          Email Import
        </span>
      </div>
      <h1 className="text-2xl font-medium text-brand-dark mb-1">Review Booking</h1>
      <p className="text-brand-muted text-sm mb-2">
        Parsed from email &mdash; review all fields and confirm.
      </p>

      {error && (
        <div className="mb-4">
          <Toast message={error} type="error" dismissError={() => setError('')} />
        </div>
      )}

      {/* Availability status */}
      {availabilityStatus === 'loading' && (
        <div className="mb-6 p-4 bg-gray-50 border border-brand-border rounded-lg text-center">
          <span className="inline-block animate-spin rounded-full h-4 w-4 border-2 border-brand-gold border-t-transparent mr-2"></span>
          Checking availability...
        </div>
      )}

      {availabilityStatus === 'available' && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <p className="font-medium text-green-800">All rooms available</p>
          <p className="text-sm text-green-700">Ready to confirm.</p>
        </div>
      )}

      {availabilityStatus === 'error' && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="font-medium text-red-800">Could not check availability</p>
          <p className="text-sm text-red-700">You can still submit, but availability is not confirmed.</p>
        </div>
      )}

      {/* Warning: main booker not in guest list */}
      {showBookerNotGuestWarning && (
        <div className="mb-4 p-4 bg-amber-50 border border-amber-300 rounded-lg text-sm">
          <p className="font-medium text-amber-800">Main booker not in guest list</p>
          <p className="text-amber-700 mt-1">
            <strong>{mainGuest.firstName} {mainGuest.lastName}</strong> is the main booker but is not listed as a guest.
            Usually the main booker is also staying. If this is correct, you can proceed.
            Otherwise, add them to the guest list below.
          </p>
        </div>
      )}

      {/* Warning: more rooms than adults */}
      {showRoomsExceedAdultsWarning && (
        <div className="mb-4 p-4 bg-amber-50 border border-amber-300 rounded-lg text-sm">
          <p className="font-medium text-amber-800">More rooms than adults</p>
          <p className="text-amber-700 mt-1">
            This booking has {totalRooms} rooms but only {adultCount} adult{adultCount === 1 ? '' : 's'}.
            Each room will be registered with at least 1 adult in Mews.
            Please verify the room count and guest list are correct.
          </p>
        </div>
      )}

      {/* Warning: price mismatch between email and Mews rate */}
      {priceMismatches.length > 0 && (
        <div className="mb-4 p-4 bg-red-50 border border-red-300 rounded-lg text-sm">
          <p className="font-medium text-red-800">Price mismatch</p>
          <p className="text-red-700 mt-1">
            The prices in the email do not match the current Mews rate:
          </p>
          <ul className="mt-2 space-y-1">
            {priceMismatches.map((m, i) => (
              <li key={i} className="text-red-700 font-mono">
                {m.name}: email {m.emailPrice.toLocaleString('da-DK')} DKK vs Mews {m.mewsPrice.toLocaleString('da-DK')} DKK
              </li>
            ))}
          </ul>
          <p className="text-red-700 mt-2">Please verify the correct price before confirming.</p>
        </div>
      )}

      {/* Parsing help notice */}
      <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm">
        <p className="text-amber-800">
          <strong>Something not right?</strong> This booking was parsed automatically from email.
          If any details look incorrect, you can edit the fields below, or{' '}
          <a href="mailto:thomas@kirsebaerkroen.dk" className="underline hover:no-underline">
            contact us
          </a>{' '}
          for assistance.
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Dates */}
        <Section title="Dates">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-brand-dark mb-2">Check-in</label>
              <input
                type="date"
                value={checkIn}
                onChange={(e) => setCheckIn(e.target.value)}
                className="input-field font-mono"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-brand-dark mb-2">Check-out</label>
              <input
                type="date"
                value={checkOut}
                onChange={(e) => setCheckOut(e.target.value)}
                className="input-field font-mono"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-brand-dark mb-2">Nights</label>
              <p className="input-field bg-gray-50 font-mono">{nights || '—'}</p>
            </div>
          </div>
        </Section>

        {/* Room Summary */}
        <Section title="Room Summary">
          <div className="space-y-3">
            {roomLines.map((line, i) => (
              <div key={i} className="flex justify-between items-center text-sm">
                <div>
                  <span className="text-brand-dark">
                    {line.quantity}&times; {line.categoryName}
                  </span>
                  <span className="text-brand-muted ml-2 text-xs font-mono">({line.flag})</span>
                </div>
                <span className="font-medium font-mono">
                  {line.totalPrice
                    ? `${Math.round(line.totalPrice * line.quantity).toLocaleString('da-DK')} ${line.currency || 'DKK'}`
                    : line.priceDKK
                    ? `${Math.round(line.priceDKK).toLocaleString('da-DK')} DKK (email)`
                    : '—'
                  }
                </span>
              </div>
            ))}
          </div>
        </Section>

        {/* PO Reference */}
        <Section title="Nordic Reference">
          <div>
            <label className="block text-sm font-medium text-brand-dark mb-2">
              PO Number <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={poReference}
              onChange={(e) => setPoReference(e.target.value)}
              placeholder="PO#######"
              className="input-field font-mono"
              required
            />
          </div>
        </Section>

        {/* Main Guest */}
        <Section title="Main Guest">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-brand-dark mb-2">
                First Name <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={mainGuest.firstName}
                onChange={(e) => setMainGuest((g) => ({ ...g, firstName: e.target.value }))}
                className="input-field"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-brand-dark mb-2">
                Last Name <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={mainGuest.lastName}
                onChange={(e) => setMainGuest((g) => ({ ...g, lastName: e.target.value }))}
                className="input-field"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-brand-dark mb-2">Telephone</label>
              <input
                type="tel"
                value={mainGuest.phone}
                onChange={(e) => setMainGuest((g) => ({ ...g, phone: e.target.value }))}
                placeholder="+32 ..."
                className="input-field"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-brand-dark mb-2">Nationality</label>
              <select
                value={mainGuest.nationalityCode}
                onChange={(e) => setMainGuest((g) => ({ ...g, nationalityCode: e.target.value }))}
                className="input-field"
              >
                <option value="">Select...</option>
                {COUNTRIES.map((c) => (
                  <option key={c.code} value={c.code}>{c.name}</option>
                ))}
              </select>
            </div>
          </div>
        </Section>

        {/* Guest List */}
        <Section title="Guests">
          <p className="text-sm text-brand-muted mb-4">
            Guest names and dates of birth from the email. Under 18 = child.
          </p>
          {guests.map((guest, index) => (
            <div key={index} className="grid grid-cols-12 gap-2 mb-3 items-end">
              <div className="col-span-3">
                {index === 0 && <label className="block text-xs font-medium text-brand-muted mb-1">First Name</label>}
                <input
                  type="text"
                  value={guest.firstName}
                  onChange={(e) => updateGuest(index, { firstName: e.target.value })}
                  placeholder="First"
                  className="input-field !py-2 text-sm"
                />
              </div>
              <div className="col-span-3">
                {index === 0 && <label className="block text-xs font-medium text-brand-muted mb-1">Last Name</label>}
                <input
                  type="text"
                  value={guest.lastName}
                  onChange={(e) => updateGuest(index, { lastName: e.target.value })}
                  placeholder="Last"
                  className="input-field !py-2 text-sm"
                />
              </div>
              <div className="col-span-3">
                {index === 0 && <label className="block text-xs font-medium text-brand-muted mb-1">Birth Date</label>}
                <input
                  type="date"
                  value={guest.birthDate}
                  onChange={(e) => {
                    const bd = e.target.value;
                    const age = bd ? Math.floor((Date.now() - new Date(bd)) / (365.25 * 24 * 60 * 60 * 1000)) : 0;
                    updateGuest(index, { birthDate: bd, age, isChild: age < 18 });
                  }}
                  className="input-field !py-2 text-sm"
                />
              </div>
              <div className="col-span-2 text-center">
                {index === 0 && <label className="block text-xs font-medium text-brand-muted mb-1">Age</label>}
                {(() => {
                  const displayAge = guest.age || (guest.birthDate
                    ? Math.floor((Date.now() - new Date(guest.birthDate)) / (365.25 * 24 * 60 * 60 * 1000))
                    : null);
                  return (
                    <span className={`text-xs ${guest.isChild ? 'text-brand-gold font-medium' : 'text-brand-muted'}`}>
                      {displayAge != null ? `${displayAge}y` : '—'} {guest.isChild ? '(C)' : ''}
                    </span>
                  );
                })()}
              </div>
              <div className="col-span-1">
                <button
                  type="button"
                  onClick={() => removeGuest(index)}
                  className="text-red-400 hover:text-red-600 text-lg transition-colors"
                  title="Remove guest"
                >
                  &times;
                </button>
              </div>
            </div>
          ))}
          <button
            type="button"
            onClick={addGuest}
            className="text-sm text-brand-gold hover:text-brand-gold-dark transition-colors"
          >
            + Add guest
          </button>
        </Section>

        {/* Breakfast */}
        <Section title="Breakfast">
          <p className="text-sm text-brand-muted">
            {breakfastCount > 0 && (
              <>The email mentions <strong>{breakfastCount}x breakfast</strong>.{' '}</>
            )}
            Breakfast is included in the Nordic rate for all guests automatically.
          </p>
        </Section>

        {/* Notes */}
        <Section title="Extra Information">
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Allergies, lactose-free, special requests, etc."
            rows={3}
            className="input-field"
          />
        </Section>

        {/* Sender info */}
        {parsed.senderEmail && (
          <Section title="Email Sender">
            <p className="text-sm text-brand-muted font-mono">{parsed.senderEmail}</p>
          </Section>
        )}

        {/* Submit */}
        <div className="mt-8 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 pt-6 border-t border-brand-border/50">
          <div>
            <p className="text-sm text-brand-muted font-mono">
              {totalRooms}R &middot; {nights}N &middot; PO: {poReference || '—'}
            </p>
            {(() => {
              // Per line: Mews-enriched price when available, else email price —
              // an all-or-nothing fallback would drop unpriced lines from the total
              const lineTotal = roomLines.reduce((sum, rl) =>
                sum + (rl.totalPrice ? rl.totalPrice * rl.quantity : (rl.priceDKK || 0)), 0);
              const displayTotal = lineTotal > 0 ? lineTotal : parsed.totalPriceDKK;
              const currency = roomLines[0]?.currency || 'DKK';
              return displayTotal > 0 ? (
                <p className="text-2xl font-medium text-brand-dark font-mono">
                  {Math.round(displayTotal).toLocaleString('da-DK')} {currency}
                </p>
              ) : null;
            })()}
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              onClick={handleBookManually}
              className="btn-outline text-sm"
            >
              Book manually instead
            </button>
            <button
              type="submit"
              disabled={submitting || availabilityStatus === 'loading'}
              className={`btn-gold ${submitting || availabilityStatus === 'loading' ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {submitting ? (
                <span className="flex items-center gap-2">
                  <span className="inline-block animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></span>
                  Creating reservation...
                </span>
              ) : (
                'Confirm Booking'
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

const Section = ({ title, children }) => (
  <div className="mb-7">
    <h2 className="text-base font-medium text-brand-dark mb-3 pb-2 border-b border-brand-border/50">{title}</h2>
    {children}
  </div>
);

const Detail = ({ label, value, mono }) => (
  <div>
    <p className="text-xs text-brand-muted uppercase tracking-wide">{label}</p>
    <p className={`text-sm text-brand-dark ${mono ? 'font-mono' : ''}`}>{value}</p>
  </div>
);

export default Import;
